'use client';

import { useState, type ReactNode } from 'react';

import { SITE_ICONS } from '@/components/site-page/siteIcons';
import type { SiteLinkView, SiteNavNodeView } from '@/components/site-page/SitePage.types';
import { Link } from '@/lib/app-router';
import { iconClasses, uploadIconClasses } from './SiteHeader.style';

interface NavAnchorProps {
  link: SiteLinkView;
  className?: string;
  'data-cy'?: string;
  /** The api node `id` (Surface Spec companion attribute of `site-header-node`). */
  'data-node-id'?: string;
  /** `GROUP` / `PAGE` / `LINK` (Surface Spec companion attribute). */
  'data-node-kind'?: string;
  'aria-label'?: string;
  'aria-current'?: 'page';
  'aria-describedby'?: string;
  onClick?: () => void;
  children: ReactNode;
}

/**
 * One resolved destination as an anchor.
 *
 * ## Invariants:
 * - `link` already passed `resolveSiteLink` / `resolveSitePagePath`, so `href` is an in-app path
 *   or an https URL, never `javascript:` or `data:`.
 * - External links open in a new tab with `noopener noreferrer` through a plain anchor (the
 *   app-router `Link` would prefix a base path onto an absolute URL).
 * - Carries `data-nav-item` so the dropdown's arrow keys can find it.
 */
export function SiteHeaderNavLink({ link, children, ...rest }: NavAnchorProps) {
  if (link.external) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" data-nav-item="" {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link href={link.href} data-nav-item="" {...rest}>
      {children}
    </Link>
  );
}

interface NodeContentProps {
  node: SiteNavNodeView;
  /** Show the visible label next to the icon. False renders icon-only. */
  showLabel: boolean;
  iconCy?: string;
  labelCy?: string;
}

/**
 * The icon and label of an item.
 *
 * ## Invariants:
 * - Label and a11yLabel render as TEXT. An uploaded icon is an `<img alt="">` (the name comes from
 *   the control), and its src was validated https by the mapper.
 * - When an uploaded icon fails to load it is removed, and an item with no visible label then
 *   shows its a11yLabel as text so it never becomes an empty control.
 */
export function SiteHeaderNodeContent({ node, showLabel, iconCy, labelCy }: NodeContentProps) {
  const [uploadFailed, setUploadFailed] = useState(false);
  const icon = node.icon;
  let iconEl: ReactNode = null;
  if (icon?.source === 'LIBRARY') {
    const Icon = SITE_ICONS[icon.name];
    if (Icon)
      iconEl = <Icon size={20} className={iconClasses} aria-hidden="true" data-cy={iconCy} />;
  } else if (icon?.source === 'UPLOAD' && !uploadFailed) {
    iconEl = (
      <img
        src={icon.url}
        alt=""
        width={icon.width ?? 20}
        height={icon.height ?? 20}
        className={uploadIconClasses}
        data-cy={iconCy}
        onError={() => setUploadFailed(true)}
      />
    );
  }
  let text: string | undefined;
  if (showLabel) text = node.label ?? (iconEl ? undefined : node.a11yLabel);
  else text = iconEl ? undefined : (node.label ?? node.a11yLabel);
  return (
    <>
      {iconEl}
      {text !== undefined && <span data-cy={labelCy}>{text}</span>}
    </>
  );
}
